import { useMemo } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { ArrowLeft } from 'lucide-react';
import { CLAIM_STATUSES, WORK_ITEM_TYPES } from '@mkcore/agents/schema';
import { Button, Card, EmptyState, ErrorState, Field, NativeSelect, PageHeader, Skeleton } from '@mkcore/ui';
import { formatDateTime } from '@/lib/format';
import { useErrorMessage } from '@/lib/mutation';
import { ClaimStatusBadge, claimsQuery } from '@/features/agents/claims';

const search = z.object({
  status: z.enum(CLAIM_STATUSES).optional(),
  itemType: z.enum(WORK_ITEM_TYPES).optional(),
});
type Search = z.infer<typeof search>;

export const Route = createFileRoute('/_app/agents/tasks/')({ validateSearch: search, component: Claims });

/**
 * The claims of agents and people on issues, tasks and tickets (CO-1235, CO-1232): open ones by default, with the
 * holder, the lease and the evidence; a claim in review waits for a person.
 */
function Claims() {
  const { t } = useTranslation();
  const s = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const message = useErrorMessage();
  const set = (patch: Partial<Search>) => void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });
  const q = useInfiniteQuery(claimsQuery({ ...(s.status && { status: s.status }), ...(s.itemType && { itemType: s.itemType }), limit: 50 }));
  const rows = useMemo(() => q.data?.pages.flatMap((p) => p.items) ?? [], [q.data]);
  return (
    <>
      <Link to="/agents" className="mb-3 inline-flex items-center gap-1 text-sm text-fg-2 hover:text-fg-1">
        <ArrowLeft className="size-4" /> {t('agents.title')}
      </Link>
      <PageHeader eyebrow={t('nav.agents')} title={t('agents.claims.title')} description={t('agents.claims.description')} />
      <div className="mb-4 flex flex-wrap gap-3">
        <Field label={t('agents.claims.status')} className="w-48">
          {(p) => (
            <NativeSelect {...p} value={s.status ?? ''} onChange={(e) => set({ status: (e.target.value || undefined) as Search['status'] })}>
              <option value="">{t('agents.claims.open')}</option>
              {CLAIM_STATUSES.map((x) => (
                <option key={x} value={x}>
                  {t(`agents.claims.statuses.${x}`)}
                </option>
              ))}
            </NativeSelect>
          )}
        </Field>
        <Field label={t('agents.claims.itemType')} className="w-40">
          {(p) => (
            <NativeSelect {...p} value={s.itemType ?? ''} onChange={(e) => set({ itemType: (e.target.value || undefined) as Search['itemType'] })}>
              <option value="">{t('agents.claims.allItems')}</option>
              {WORK_ITEM_TYPES.map((x) => (
                <option key={x} value={x}>
                  {t(`agents.claims.items.${x}`)}
                </option>
              ))}
            </NativeSelect>
          )}
        </Field>
      </div>
      {q.isError ? (
        <ErrorState title={t('errors.generic')} description={message(q.error)} />
      ) : q.isLoading ? (
        <Skeleton className="h-48" />
      ) : !rows.length ? (
        <EmptyState title={t('agents.claims.none')} description={t('agents.claims.noneHint')} />
      ) : (
        <ul className="grid gap-2" aria-label={t('agents.claims.title')}>
          {rows.map((c) => (
            <li key={c.id}>
              <Card className="grid gap-1 p-3">
                <span className="flex flex-wrap items-center gap-2">
                  <Link to="/agents/tasks/$id" params={{ id: c.id }} className="font-bold hover:underline">
                    {c.item ? `${c.item.key} ${c.item.title}` : t('agents.claims.hiddenItem')}
                  </Link>
                  <ClaimStatusBadge status={c.status} />
                </span>
                <span className="flex flex-wrap gap-3 text-xs text-fg-3">
                  <span>{t('agents.claims.holder', { name: c.holderName })}</span>
                  <span>{t('agents.claims.since', { date: formatDateTime(c.claimedAt) })}</span>
                  {c.leaseUntil && <span>{t('agents.claims.lease', { date: formatDateTime(c.leaseUntil) })}</span>}
                  {!!c.evidence.length && <span>{t('agents.claims.evidenceCount', { count: c.evidence.length })}</span>}
                </span>
              </Card>
            </li>
          ))}
        </ul>
      )}
      {q.hasNextPage && (
        <div className="mt-3 flex justify-center">
          <Button variant="secondary" loading={q.isFetchingNextPage} onClick={() => void q.fetchNextPage()}>
            {t('common.more')}
          </Button>
        </div>
      )}
    </>
  );
}
